import { useContext, useMemo, useState, useEffect } from 'react';
import { CheckoutContext } from '@/context/CheckoutContext';
import CheckmarkCircle from '@/assets/icons/common/checkmark-circle.svg';
import SuccessConfetti from '@/assets/icons/common/successConfeti.gif';
import { ProviderEnum } from '@/types/common';
import type { PaymentIntentResult } from '@stripe/stripe-js';
import { redirectUser } from '@/utils/location';
import { shouldShowSuccessScreen } from '@/utils/conditionalRendering';
import { useTranslate } from '@/hooks/useTranslate';
import { getCheckoutSolution } from '@/utils/checkoutSolution';
import { useAutoRedirect } from '@/hooks/useAutoRedirect';

const CONFETTI_DURATION = 1000;

export const SuccessScreen = () => {
  const context = useContext(CheckoutContext);
  const {
    successScreen: { paymentDetails, provider },
    featureFlags,
    checkoutConfiguration,
    actions: { closeCheckout },
  } = context;
  const [showConfetti, setShowConfetti] = useState(true);
  const { t } = useTranslate();
  const { isPaymentLinks } = getCheckoutSolution(checkoutConfiguration);
  const { isRedirectAttempted, fullyRedirectLink } = useAutoRedirect();

  const paymentId = useMemo(() => {
    if (!paymentDetails) return '';
    if (provider === ProviderEnum.STRIPE) {
      const params = paymentDetails.params as PaymentIntentResult;
      return params?.paymentIntent?.id || '';
    }
    return '';
  }, [paymentDetails, provider]);

  const confettiSrc = useMemo(() => `${SuccessConfetti}?t=${Date.now()}`, []);

  const handleCloseCheckout = () => {
    if (isPaymentLinks && fullyRedirectLink) {
      redirectUser(fullyRedirectLink ?? '');
    }
    closeCheckout(context);
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowConfetti(false);
    }, CONFETTI_DURATION);

    return () => clearTimeout(timer);
  }, []);

  if (!shouldShowSuccessScreen(featureFlags)) return null;

  return (
    <div
      data-testid="success-screen"
      className="fixed inset-0 bg-base-black/80 backdrop-blur-sm flex items-center justify-center gap-4 z-200"
    >
      {showConfetti && (
        <img
          data-testid="success-confetti"
          src={confettiSrc}
          className="absolute inset-0 w-full h-full object-contain pointer-events-none"
        />
      )}
      <div className="relative flex flex-col items-center justify-center gap-6 px-8">
        <img data-testid="success-icon" src={CheckmarkCircle} className="w-16 h-16" />
        <div className="flex flex-col items-center gap-4">
          <h1 data-testid="success-title" className="text-3xl font-semibold text-white text-center">
            {t('purchaseCompleted')}
          </h1>
          <div
            data-testid="back-to-store-button"
            className="h-14 w-[70%] flex flex-row items-center justify-center gap-2 border-1 border-white rounded-xl cursor-pointer"
            onClick={handleCloseCheckout}
          >
            <div className="text-white text-base">
              {isPaymentLinks ? (isRedirectAttempted ? t('backToGame') : null) : t('backToStore')}
            </div>
          </div>
          {paymentId && (
            <div
              data-testid="order-id"
              className="flex flex-row items-center justify-center gap-2 mt-1 text-white text-xs"
            >
              {t('orderId', { id: paymentId })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
